/**
 * Gradient Utilities — Level detection, preset builders, Figma helpers
 *
 * Pure utility functions for gradient data manipulation.
 * No React imports, no UI concerns.
 */

import type {
  GradientOverlay,
  GradientStop,
  ShimmerConfig,
} from '@segmently/schema-models/v2';

// ============================================
// Types
// ============================================

export type GradientLevel = 'simple' | 'medium' | 'advanced';

// ============================================
// Angle Presets
// ============================================

export const ANGLE_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: '↑ Top' },
  { value: 45, label: '↗ Top Right' },
  { value: 90, label: '→ Right' },
  { value: 135, label: '↘ Bottom Right' },
  { value: 180, label: '↓ Bottom' },
  { value: 225, label: '↙ Bottom Left' },
  { value: 270, label: '← Left' },
  { value: 315, label: '↖ Top Left' },
];

const PRESET_ANGLES = new Set(ANGLE_PRESETS.map((p) => p.value));

// ============================================
// Default Shimmer Configs
// ============================================

export const DEFAULT_SHIMMER_HOVER: ShimmerConfig = {
  enabled: true,
  trigger: 'hover',
  color: 'rgba(255, 255, 255, 0.2)',
  width: 0.6,
  skew: -20,
  duration: 0.5,
  cycle: 4,
};

export const DEFAULT_SHIMMER_TIMER: ShimmerConfig = {
  enabled: true,
  trigger: 'timer',
  color: 'rgba(255, 255, 255, 0.15)',
  width: 0.5,
  skew: -20,
  duration: 0.6,
  cycle: 4,
};

// ============================================
// Level Detection (derive from data, NOT stored)
// ============================================

/**
 * Detect gradient complexity level from the stored config.
 * Used to pre-select the correct editor tab (Simple/Medium/Advanced).
 */
export function detectGradientLevel(gradient: GradientOverlay | null | undefined): GradientLevel {
  if (!gradient) return 'simple';

  const stopCount = gradient.stops?.length ?? gradient.colors?.length ?? 0;
  const hasShimmer = gradient.shimmer?.enabled === true;
  const angle = gradient.angle;

  // >2 stops → advanced
  if (stopCount > 2) return 'advanced';

  // Non-preset angle → advanced
  if (angle !== undefined && !PRESET_ANGLES.has(angle)) return 'advanced';

  // Shimmer with non-default trigger or custom params → advanced
  if (hasShimmer && gradient.shimmer?.trigger === 'timer') return 'advanced';

  // Has shimmer with defaults → medium
  if (hasShimmer) return 'medium';

  // Has non-default angle → medium
  if (angle !== undefined && angle !== 90) return 'medium';

  return 'simple';
}

// ============================================
// Preset → Full Config Builders
// ============================================

/**
 * Build a simple 2-color gradient (90° angle, no shimmer).
 */
export function buildSimpleGradient(startColor: string, endColor: string): GradientOverlay {
  return {
    colors: [startColor, endColor],
    stops: [
      { color: startColor, position: 0 },
      { color: endColor, position: 1 },
    ],
    angle: 90,
  };
}

/**
 * Build a medium gradient (custom angle, optional hover shimmer).
 */
export function buildMediumGradient(
  startColor: string,
  endColor: string,
  angle: number,
  shimmerEnabled: boolean,
): GradientOverlay {
  return {
    colors: [startColor, endColor],
    stops: [
      { color: startColor, position: 0 },
      { color: endColor, position: 1 },
    ],
    angle,
    shimmer: shimmerEnabled ? { ...DEFAULT_SHIMMER_HOVER } : undefined,
  };
}

// ============================================
// Default Gradient
// ============================================

export const DEFAULT_GRADIENT: GradientOverlay = buildSimpleGradient('#10B981', '#0EA5E9');

/**
 * Build a gradient derived from a base color (e.g., button background).
 * Creates a subtle top-to-bottom gradient: baseColor → darkened ~15%.
 *
 * @param baseColor - Hex color string (e.g., '#ffc201')
 * @returns GradientOverlay with 180° angle (top→bottom)
 */
export function buildGradientFromBaseColor(baseColor: string): GradientOverlay {
  const darkened = darkenHexColor(baseColor, 0.15);
  return {
    colors: [baseColor, darkened],
    stops: [
      { color: baseColor, position: 0 },
      { color: darkened, position: 1 },
    ],
    angle: 180,
  };
}

/**
 * Darken a hex color by a given factor (0-1).
 * E.g., darkenHexColor('#ffc201', 0.15) → '#d9a501' (15% darker)
 */
function darkenHexColor(hex: string, factor: number): string {
  // Strip # and handle 3/6-char hex
  const clean = hex.replace('#', '');
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;

  const r = Math.max(0, Math.round(parseInt(full.slice(0, 2), 16) * (1 - factor)));
  const g = Math.max(0, Math.round(parseInt(full.slice(2, 4), 16) * (1 - factor)));
  const b = Math.max(0, Math.round(parseInt(full.slice(4, 6), 16) * (1 - factor)));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// ============================================
// Stops ↔ Colors+Locations Sync
// ============================================

/**
 * Convert structured stops[] to flat colors[] + locations[] arrays.
 * Keeps backward compatibility with existing rendering code.
 */
export function stopsToColorsLocations(stops: GradientStop[]): {
  colors: string[];
  locations: number[];
} {
  const sorted = [...stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  return {
    colors: sorted.map((s) => s.color),
    locations: sorted.map((s) => s.position ?? 0),
  };
}

/**
 * Convert flat colors[] + locations[] to structured stops[].
 */
export function colorsLocationsToStops(
  colors: string[],
  locations?: number[],
): GradientStop[] {
  return colors.map((color, i) => ({
    color,
    position: locations?.[i] ?? (colors.length > 1 ? i / (colors.length - 1) : 0),
  }));
}

/**
 * Extract start and end colors from a gradient config.
 * Handles both stops[] and colors[] representations.
 */
export function extractColors(gradient: GradientOverlay | null | undefined): {
  startColor: string;
  endColor: string;
} {
  const defaultStart = '#10B981';
  const defaultEnd = '#0EA5E9';

  if (!gradient) return { startColor: defaultStart, endColor: defaultEnd };

  if (gradient.stops && gradient.stops.length >= 2) {
    const sorted = [...gradient.stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    return {
      startColor: sorted[0].color ?? defaultStart,
      endColor: sorted[sorted.length - 1].color ?? defaultEnd,
    };
  }

  if (gradient.colors && gradient.colors.length >= 2) {
    return {
      startColor: gradient.colors[0] ?? defaultStart,
      endColor: gradient.colors[gradient.colors.length - 1] ?? defaultEnd,
    };
  }

  if (gradient.colors && gradient.colors.length === 1) {
    return { startColor: gradient.colors[0], endColor: gradient.colors[0] };
  }

  return { startColor: defaultStart, endColor: defaultEnd };
}

/**
 * Extract stops from gradient config, normalizing from either format.
 */
export function extractStops(gradient: GradientOverlay | null | undefined): GradientStop[] {
  if (!gradient) {
    return [
      { color: '#10B981', position: 0 },
      { color: '#0EA5E9', position: 1 },
    ];
  }

  if (gradient.stops && gradient.stops.length >= 2) {
    return [...gradient.stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  return colorsLocationsToStops(gradient.colors ?? ['#10B981', '#0EA5E9'], gradient.locations);
}

// ============================================
// Figma Import Helper
// ============================================

/**
 * Convert Figma gradientHandlePositions (normalized 0-1 coordinates) to CSS angle in degrees.
 *
 * Figma stores gradient as 3 handle positions in normalized object space (0,0 = top-left, 1,1 = bottom-right).
 * Position 0 = gradient start, Position 1 = gradient end.
 *
 * CSS angle convention: 0deg = to top, 90deg = to right, 180deg = to bottom.
 */
export function figmaHandlesToAngle(
  start: { x: number; y: number },
  end: { x: number; y: number },
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  // atan2 gives angle from positive x-axis, CSS 0deg points up
  const radians = Math.atan2(dy, dx);
  const degrees = (radians * 180) / Math.PI;
  // Convert: atan2 0° = right → CSS 90°, atan2 90° = down → CSS 180°
  const cssAngle = (degrees + 90 + 360) % 360;
  return Math.round(cssAngle);
}

// ============================================
// CSS Preview Helper
// ============================================

/**
 * Generate a CSS linear-gradient string from a GradientOverlay config.
 * Used for preview rendering in the editor.
 */
export function gradientToCSS(gradient: GradientOverlay | null | undefined): string {
  if (!gradient) return 'transparent';

  const angle = gradient.angle ?? 90;
  const stops = extractStops(gradient);

  const stopsCSS = stops
    .map((s) => `${s.color} ${Math.round((s.position ?? 0) * 100)}%`)
    .join(', ');

  return `linear-gradient(${angle}deg, ${stopsCSS})`;
}
